import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Pagination, Paper, Skeleton, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdInventory2, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { getData } from '../../Webservice/webservice';

const FONT = 'Mulish, sans-serif';
const authToken = () => { const raw = sessionStorage.getItem('token') || localStorage.getItem('sellerToken'); return raw ? `Bearer ${raw}` : ''; };
const inr = (v) => `${Number(v) < 0 ? '-' : ''}₹${Math.abs(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const METHOD = { bank_transfer: 'Bank transfer', upi: 'UPI', manual: 'Manual' };

const TONE = {
  pending: ['Pending', '#fef3c7', '#92400e'], settled: ['Paid', '#dcfce7', '#166534'],
  initiated: ['Started', '#fef3c7', '#92400e'], processing: ['Processing', '#dbeafe', '#1e40af'], completed: ['Paid', '#dcfce7', '#166534'], failed: ['Failed', '#fee2e2', '#991b1b'],
  order: ['Order', '#e0e7ff', '#3730a3'], return: ['Return', '#fee2e2', '#991b1b'], payout: ['Payout', '#dcfce7', '#166534'],
};
const Pill = ({ k }) => {
  const [label, bg, fg] = TONE[String(k || '').toLowerCase()] || [k || '—', '#f1f5f9', '#475569'];
  return <Chip size="small" label={label} sx={{ fontFamily: FONT, fontWeight: 800, height: 22, background: bg, color: fg }} />;
};

const Kpi = ({ title, value, sub, from, to, loading }) => (
  <Box sx={{ color: '#fff', p: 2, borderRadius: '14px', background: `linear-gradient(135deg,${from},${to})`, minWidth: 0 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, opacity: 0.92 }}>{title}</Typography>
    {loading ? <Skeleton width={110} height={36} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} /> : <Typography sx={{ fontFamily: FONT, fontSize: 25, fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>}
    {sub && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.88 }}>{sub}</Typography>}
  </Box>
);
const Row = ({ label, value, color, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.85, borderBottom: '1px dashed #e2e8f0', '&:last-of-type': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: bold ? 800 : 700, color: color || '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);
const Block = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>{title}</Typography>
    {children}
  </Box>
);
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12, color: '#64748b', background: '#f8fafc' };

const Payout = () => {
  const [tab, setTab] = useState('transactions');
  const [error, setError] = useState('');

  // summary (the numbers do not depend on filters)
  const [pending, setPending] = useState(0);
  const [pStats, setPStats] = useState({});
  const [lStats, setLStats] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);

  // transactions
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState(null);

  // payouts
  const [payouts, setPayouts] = useState([]);
  const [pMeta, setPMeta] = useState({ total: 0, totalPages: 1 });
  const [pLoading, setPLoading] = useState(false);
  const [pPage, setPPage] = useState(1);
  const [pStatus, setPStatus] = useState('');
  const [selectedPayout, setSelectedPayout] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('seller/payout/ledger-history', { page, limit: 20, ...(debounced && { search: debounced }), ...(type && { type }), ...(status && { status }), ...(from && { from }), ...(to && { to }) }, authToken());
      setRows(res?.ledger || []);
      setMeta(res?.meta || { total: 0, totalPages: 1 });
      setLStats(res?.stats || {});
      setError('');
    } catch (e) {
      setError(e?.message || 'Could not load your transactions.');
      setRows([]);
    } finally { setLoading(false); }
  }, [page, debounced, type, status, from, to]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const loadPayouts = useCallback(async () => {
    setPLoading(true);
    try {
      const res = await getData('seller/payout/payout-history', { page: pPage, limit: 20, ...(pStatus && { status: pStatus }) }, authToken());
      setPayouts(res?.payouts || []);
      setPMeta(res?.meta || { total: 0, totalPages: 1 });
      setPStats(res?.stats || {});
    } catch (e) {
      setError(e?.message || 'Could not load your payouts.');
      setPayouts([]);
    } finally { setPLoading(false); setSummaryLoading(false); }
  }, [pPage, pStatus]);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  useEffect(() => {
    getData('seller/payout/pending-balance', null, authToken()).then((r) => setPending(Number(r?.pendingBalance) || 0)).catch(() => setPending(0));
  }, []);

  const refresh = () => { loadTransactions(); loadPayouts(); getData('seller/payout/pending-balance', null, authToken()).then((r) => setPending(Number(r?.pendingBalance) || 0)).catch(() => {}); };
  const dirty = !!(search || type || status || from || to);
  const reset = () => { setSearch(''); setDebounced(''); setType(''); setStatus(''); setFrom(''); setTo(''); setPage(1); };
  const pf = (setter) => (e) => { setter(e.target.value); setPage(1); };

  const productOf = (r) => r.sellerRequestId?.product_id;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Payouts and transactions</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>What you have earned from orders, and what has been paid to you.</Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={refresh} sx={{ background: '#fff', border: '1px solid #e2e8f0' }}><MdRefresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')} action={<Button color="inherit" size="small" onClick={refresh}>Try again</Button>}>{error}</Alert>}

      <Box data-tour="vendor-payout-balance" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, mb: 2.5 }}>
        <Kpi title="Waiting to be paid" value={inr(pending)} sub="your pending balance" from="#f59e0b" to="#b45309" loading={summaryLoading} />
        <Kpi title="Paid to you" value={inr(pStats.paid)} sub={pStats.paidCount ? `${pStats.paidCount} payout${pStats.paidCount > 1 ? 's' : ''}${pStats.lastPaidAt ? ` · last ${date(pStats.lastPaidAt)}` : ''}` : 'nothing paid yet'} from="#10b981" to="#047857" loading={summaryLoading} />
        <Kpi title="On its way" value={inr(pStats.inProcess)} sub="payouts being processed" from="#6366f1" to="#4338ca" loading={summaryLoading} />
        <Kpi title="Platform commission" value={inr(lStats.commission)} sub={`on ${lStats.orders || 0} order${lStats.orders === 1 ? '' : 's'}`} from="#ec4899" to="#be185d" loading={loading && !lStats.orders} />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Tab data-tour="vendor-payout-ledger" value="transactions" label={`Transactions${meta.total ? ` (${meta.total})` : ''}`} sx={{ textTransform: 'none', fontWeight: 800 }} />
        <Tab data-tour="vendor-payout-history" value="payouts" label={`Payouts${pMeta.total ? ` (${pMeta.total})` : ''}`} sx={{ textTransform: 'none', fontWeight: 800 }} />
      </Tabs>

      {tab === 'transactions' && (
        <>
          <Box data-tour="vendor-payout-filters" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
            <TextField size="small" placeholder="Search order request, order number or product…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
            <TextField select size="small" label="Type" value={type} onChange={pf(setType)} sx={{ minWidth: 130 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="order">Orders</MenuItem><MenuItem value="return">Returns</MenuItem>
            </TextField>
            <TextField select size="small" label="Status" value={status} onChange={pf(setStatus)} sx={{ minWidth: 130 }}>
              <MenuItem value="">All</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="settled">Paid</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="From" value={from} onChange={pf(setFrom)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="To" value={to} onChange={pf(setTo)} InputLabelProps={{ shrink: true }} />
            {dirty && <Button onClick={reset} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>}
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>{['Date', 'Type', 'Order request', 'Product', 'Gross', 'Commission', 'You get', 'Status', ''].map((h) => <TableCell key={h} sx={head} align={['Gross', 'Commission', 'You get'].includes(h) ? 'right' : 'left'}>{h}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {loading && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 9 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                  {!loading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={9}><Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 6 }}>{dirty ? 'No transaction matches those filters.' : 'No transactions yet. They appear when your orders are delivered.'}</Typography></TableCell></TableRow>
                  )}
                  {!loading && rows.map((r) => {
                    const prod = productOf(r);
                    return (
                      <TableRow key={r._id} hover>
                        <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{date(r.createdAt)}</TableCell>
                        <TableCell><Pill k={r.type} /></TableCell>
                        <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, fontWeight: 700, color: '#4f46e5' }}>{r.sellerRequestId?.receiptId ? `#${r.sellerRequestId.receiptId}` : '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 170 }}>
                            <Avatar variant="rounded" src={prod?.product_images?.[0]} sx={{ width: 32, height: 32 }}><MdInventory2 size={16} /></Avatar>
                            <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{prod?.title || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT }}>{inr(r.grossAmount)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, color: '#b91c1c' }}>{r.commissionAmount ? `${inr(-Math.abs(r.commissionAmount))}` : '—'}<Typography component="div" sx={{ fontSize: 11, color: '#94a3b8' }}>{r.commissionPercentage || 0}%</Typography></TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, fontWeight: 800, color: r.netAmount < 0 ? '#b91c1c' : '#15803d' }}>{inr(r.netAmount)}</TableCell>
                        <TableCell><Pill k={r.status} /></TableCell>
                        <TableCell align="right"><Tooltip title="Details"><IconButton size="small" onClick={() => setSelected(r)} sx={{ color: '#4f46e5' }}><MdVisibility size={18} /></IconButton></Tooltip></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {meta.totalPages > 1 && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, flexWrap: 'wrap', gap: 1 }}><Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} transactions</Typography><Pagination count={meta.totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" /></Box>}
          </Paper>
        </>
      )}

      {tab === 'payouts' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {[['', 'All'], ['completed', 'Paid'], ['processing', 'Processing'], ['initiated', 'Started'], ['failed', 'Failed']].map(([v, l]) => (
              <Chip key={l} clickable label={l} onClick={() => { setPStatus(v); setPPage(1); }} sx={{ fontFamily: FONT, fontWeight: 800, background: pStatus === v ? '#0f172a' : '#fff', color: pStatus === v ? '#fff' : '#334155', border: '1px solid #cbd5e1' }} />
            ))}
          </Box>
          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>{['Date', 'Reference', 'Method', 'Amount', 'Status', 'Paid on', ''].map((h) => <TableCell key={h} sx={head} align={h === 'Amount' ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {pLoading && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                  {!pLoading && payouts.length === 0 && <TableRow><TableCell colSpan={7}><Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 6 }}>No payouts yet.</Typography></TableCell></TableRow>}
                  {!pLoading && payouts.map((p) => (
                    <TableRow key={p._id} hover>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{date(p.createdAt)}</TableCell>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5 }}>{p.referenceNumber || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>{METHOD[p.paymentMethod] || p.paymentMethod || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: FONT, fontWeight: 800 }}>{inr(p.amount)}</TableCell>
                      <TableCell><Pill k={p.status} /></TableCell>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{date(p.settledAt)}</TableCell>
                      <TableCell align="right"><Tooltip title="Details"><IconButton size="small" onClick={() => setSelectedPayout(p)} sx={{ color: '#4f46e5' }}><MdVisibility size={18} /></IconButton></Tooltip></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pMeta.totalPages > 1 && <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}><Pagination count={pMeta.totalPages} page={pPage} onChange={(_, v) => setPPage(v)} shape="rounded" /></Box>}
          </Paper>
        </>
      )}

      {/* transaction details */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        {selected && (
          <>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.25 }}>Transaction <Pill k={selected.type} /> <Pill k={selected.status} /></DialogTitle>
            <DialogContent>
              <Block title="How it adds up">
                <Row label={selected.type === 'return' ? 'Returned order value' : 'Order value'} value={inr(selected.grossAmount)} />
                <Row label={`Platform commission (${selected.commissionPercentage || 0}%)`} value={selected.commissionAmount ? inr(-Math.abs(selected.commissionAmount)) : inr(0)} color="#b91c1c" />
                <Row label={selected.type === 'return' ? 'Taken back from you' : 'You get'} value={inr(selected.netAmount)} color={selected.netAmount < 0 ? '#b91c1c' : '#15803d'} bold />
              </Block>
              <Block title="Product">
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.25, borderRadius: '12px', background: '#f8fafc' }}>
                  <Avatar variant="rounded" src={productOf(selected)?.product_images?.[0]} sx={{ width: 52, height: 52 }}><MdInventory2 /></Avatar>
                  <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14 }}>{productOf(selected)?.title || 'Product not recorded'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{productOf(selected)?.upc ? `UPC ${productOf(selected).upc} · ` : ''}Quantity {selected.meta?.quantity || selected.sellerRequestId?.quantity || '—'}</Typography>
                  </Box>
                </Box>
              </Block>
              <Block title="Order and request">
                <Row label="Order request" value={selected.sellerRequestId?.receiptId ? `#${selected.sellerRequestId.receiptId}` : null} />
                <Row label="Order number" value={selected.orderId?.orderNumber} />
                <Row label="Order date" value={date(selected.orderId?.createdAt || selected.createdAt)} />
                <Row label="Recorded" value={dateTime(selected.createdAt)} />
              </Block>
              <Block title="Payment">
                <Row label="Status" value={selected.status === 'settled' ? `Paid${selected.settledAt ? ` on ${date(selected.settledAt)}` : ''}` : 'Waiting to be included in a payout'} />
              </Block>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setSelected(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>

      {/* payout details */}
      <Dialog open={!!selectedPayout} onClose={() => setSelectedPayout(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        {selectedPayout && (
          <>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.25 }}>Payout <Pill k={selectedPayout.status} /></DialogTitle>
            <DialogContent>
              <Typography sx={{ fontFamily: FONT, fontSize: 30, fontWeight: 800, color: selectedPayout.status === 'failed' ? '#b91c1c' : '#0f172a', mb: 1 }}>{inr(selectedPayout.amount)}</Typography>
              {selectedPayout.status === 'failed' && <Alert severity="error" sx={{ mb: 1.5 }}>This payout did not go through{selectedPayout.remarks ? `: ${selectedPayout.remarks}` : '.'} Our team will retry it, or contact support.</Alert>}
              <Block title="Transfer">
                <Row label="Method" value={METHOD[selectedPayout.paymentMethod] || selectedPayout.paymentMethod} />
                <Row label="Reference" value={selectedPayout.referenceNumber} />
                <Row label="Started" value={dateTime(selectedPayout.createdAt)} />
                <Row label="Paid on" value={selectedPayout.settledAt ? dateTime(selectedPayout.settledAt) : null} />
                {selectedPayout.remarks && selectedPayout.status !== 'failed' && <Row label="Note" value={selectedPayout.remarks} />}
              </Block>
              {selectedPayout.bankSnapshot && (
                <Block title="Sent to">
                  <Row label="Account holder" value={selectedPayout.bankSnapshot.accountHolder} />
                  <Row label="Bank" value={selectedPayout.bankSnapshot.bankName} />
                  <Row label="Account number" value={selectedPayout.bankSnapshot.accountNumber} />
                  <Row label="IFSC" value={selectedPayout.bankSnapshot.ifsc} />
                  {selectedPayout.bankSnapshot.upiId && <Row label="UPI" value={selectedPayout.bankSnapshot.upiId} />}
                </Block>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setSelectedPayout(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Payout;
